// Flipping from one league's Game Day to the next on a Sunday (F7,
// MATCHUP.md "Flipping to the next league's Game Day", DIRECTION U31, B-607).
//
// A STRIP OVER MY OWN GAME DAY, while I have two or more leagues with a game
// to follow: the card row's arrows and dots (B-405), stopping at the ends as
// it does. A phone's arrows alone with "2 of 3 leagues"; from a tablet up each
// names the league it goes to. The strip stays put while a phone's Game Day
// scrolls under it.
//
// THREE WAYS, NONE THE ONLY ONE (UX-3): the arrows; a swipe across the Game
// Day on a touch screen; the left and right arrow keys while nothing that
// takes them itself has focus.
//
// THE FLIP REPLACES THE ADDRESS, so Back leaves Game Day for wherever it was
// opened from rather than stepping back through every league flipped past.
// Each opens its own week being played, and slides in from its side once.

import { useEffect, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub, useRouteLeagueId } from '../../data/hooks';
import { Button, IconButton } from '../../kit/ui';
import { useTier } from '../../shell/useTier';
import { gameDaysOf, type FlipStop } from './flip';

/** How far a finger travels across before it is a swipe, and how straight. */
const SWIPE_PX = 64;
/** A touch this near a side is the browser's own back gesture, not ours. */
const EDGE_PX = 24;
const SWIPE_MS = 700;

/** What takes the arrow keys, or a sideways drag, for itself. */
const OWNS_ARROWS =
  'input, textarea, select, [contenteditable="true"], [role="tablist"], [role="radiogroup"], ' +
  '[role="slider"], [role="listbox"], [role="menu"], [role="grid"], [role="dialog"], ' +
  '[aria-roledescription="carousel"]';

/** Whether a sideways swipe that starts at `target` is the flip's to take. */
function swipeIsOurs(target: EventTarget | null, root: HTMLElement): boolean {
  if (!(target instanceof Element) || !root.contains(target)) return false;
  for (let el: Element | null = target; el !== null && el !== root; el = el.parentElement) {
    if (el.matches(OWNS_ARROWS)) return false;
    // A region that scrolls sideways keeps its own swipe.
    const x = getComputedStyle(el).overflowX;
    if ((x === 'auto' || x === 'scroll') && el.scrollWidth > el.clientWidth + 1) return false;
  }
  return true;
}

/**
 * The slide a flip asked for, taken once by the Game Day it opened: Back to
 * it, or a reload, does not slide it in again, and the shell keeps still for a
 * replaced address, so nothing moves twice (the review of 2026-11-01).
 */
let slide: { to: string; from: 'left' | 'right' } | null = null;

/** One league's Game Day under the strip, sliding in when a flip opened it. */
function FlipPage({ league, children }: { league: string | null; children: ReactNode }) {
  const [from] = useState(() => (slide !== null && slide.to === league ? slide.from : null));
  useEffect(() => {
    if (slide?.to === league) slide = null;
  }, [league]);
  return (
    <div className="fsp-gd-flip-page" data-from={from ?? undefined}>
      {children}
    </div>
  );
}

export function GameDayFlip({ children }: { children: ReactNode }) {
  const hub = useHub();
  const current = useRouteLeagueId();
  const flip = gameDaysOf(hub.leagues, current);
  const navigate = useNavigate();
  const tier = useTier();
  const root = useRef<HTMLDivElement>(null);
  const nav = useRef<HTMLElement>(null);
  const start = useRef<{ x: number; y: number; at: number } | null>(null);
  // The arrow last pressed, to keep the keyboard's place on the strip.
  const pressed = useRef<'prev' | 'next' | null>(null);

  const prev: FlipStop | null = flip !== null && flip.at > 0 ? flip.stops[flip.at - 1]! : null;
  const next: FlipStop | null =
    flip !== null && flip.at < flip.stops.length - 1 ? flip.stops[flip.at + 1]! : null;
  const go = (to: FlipStop | null, from: 'left' | 'right') => {
    if (to === null) return;
    slide = { to: to.id, from };
    navigate(`/l/${to.id}/matchup`, { replace: true });
  };

  // THE ARROW KEYS, while nothing that takes them has focus and nothing is
  // open over the page.
  const keys = useRef({ prev, next, go });
  keys.current = { prev, next, go };
  const flipping = flip !== null;
  useEffect(() => {
    if (!flipping) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.target instanceof Element && e.target.closest(OWNS_ARROWS)) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const { prev: p, next: n, go: g } = keys.current;
      const to = e.key === 'ArrowLeft' ? p : n;
      if (to === null) return;
      e.preventDefault();
      g(to, e.key === 'ArrowLeft' ? 'left' : 'right');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flipping]);

  // AN ARROW PRESSED KEEPS THE FOCUS ON THE STRIP: the one pressed, or at an
  // end, where it turns off, the other, rather than the page's body.
  const ends = { prev: prev !== null, next: next !== null };
  useEffect(() => {
    const p = pressed.current;
    pressed.current = null;
    const buttons = nav.current?.querySelectorAll('button');
    if (p === null || buttons === undefined || buttons.length < 2) return;
    const before = buttons[0]!;
    const after = buttons[buttons.length - 1]!;
    const onward = p === 'next' ? ends.next : ends.prev;
    const same = p === 'next' ? after : before;
    (onward ? same : same === after ? before : after).focus();
    // Once each flip lands: the league on screen is what changed.
  }, [current]);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    start.current =
      flip !== null &&
      e.touches.length === 1 &&
      t !== undefined &&
      root.current !== null &&
      t.clientX > EDGE_PX &&
      t.clientX < window.innerWidth - EDGE_PX &&
      swipeIsOurs(e.target, root.current)
        ? { x: t.clientX, y: t.clientY, at: Date.now() }
        : null;
  };
  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const s = start.current;
    start.current = null;
    const t = e.changedTouches[0];
    if (s === null || t === undefined || Date.now() - s.at > SWIPE_MS) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < 2 * Math.abs(dy)) return;
    // To the left brings the next one in from the right.
    if (dx < 0) go(next, 'right');
    else go(prev, 'left');
  };
  const press = (which: 'prev' | 'next') => {
    pressed.current = which;
    if (which === 'prev') go(prev, 'left');
    else go(next, 'right');
  };

  const phone = tier === 'phone';
  // THE SAME TREE WITH THE STRIP OR WITHOUT IT, so a hub refresh that adds or
  // takes away a league does not remount the Game Day under it.
  return (
    <div
      ref={root}
      className="fsp-screen fsp-gd-flipper"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={() => {
        start.current = null;
      }}
    >
      {flip === null ? null : (
        <nav ref={nav} className="fsp-gd-flip" aria-label="Your leagues' Game Days">
          {phone ? (
            <IconButton
              icon="previous"
              label={prev ? `Previous league: ${prev.name}` : 'Previous league'}
              variant="quiet"
              disabled={prev === null}
              onClick={() => press('prev')}
            />
          ) : (
            <Button
              variant="quiet"
              size="xs"
              icon="previous"
              disabled={prev === null}
              onClick={() => press('prev')}
              label={prev ? `Previous league: ${prev.name}` : 'Previous league'}
            >
              <span className="fsp-gd-flip-name">{prev?.name ?? 'Previous'}</span>
            </Button>
          )}
          <span className="fsp-gd-flip-where">
            <span className="fsp-cards-dots" aria-hidden>
              {flip.stops.map((s, i) => (
                <span key={s.id} className="fsp-cards-dot" data-on={i === flip.at || undefined} />
              ))}
            </span>
            {phone ? (
              <span className="fsp-gd-flip-count" aria-hidden>
                {`${flip.at + 1} of ${flip.stops.length} leagues`}
              </span>
            ) : null}
            <span className="fsp-sr" aria-live="polite">
              {`Game Day ${flip.at + 1} of ${flip.stops.length}: ${flip.stops[flip.at]!.name}`}
            </span>
          </span>
          {phone ? (
            <IconButton
              icon="next"
              label={next ? `Next league: ${next.name}` : 'Next league'}
              variant="quiet"
              disabled={next === null}
              onClick={() => press('next')}
            />
          ) : (
            <Button
              variant="quiet"
              size="xs"
              iconRight="next"
              disabled={next === null}
              onClick={() => press('next')}
              label={next ? `Next league: ${next.name}` : 'Next league'}
            >
              <span className="fsp-gd-flip-name">{next?.name ?? 'Next'}</span>
            </Button>
          )}
        </nav>
      )}
      <FlipPage key={current ?? 'none'} league={current}>
        {children}
      </FlipPage>
    </div>
  );
}
